import moment from 'moment';
import { useContext } from 'react';
import { useEffect, useState } from 'react';
import { axios } from '../../../../../utils/axios';
import { useParams } from 'react-router-dom';

import { Tabs } from '@mantine/core';
import { tabStyles } from '../../../../../utils/helpers.js';
import IntermediaryCountry from '../tpErpSetup/intermediaryCountry/index';
import BuyingCountry from '../tpErpSetup/buyingCountry/index';
import SellingCountry from './sellingCountry/index.js';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest.js';
import PIRSetup from './pirSetup/index.js';

export const TpErpSetup = ({ setLogsModal, activeTab, activeSubTab }) => {
  const { crNumber } = useParams();
  const { info, crAssignedSpocs } = useContext(CostingRequestsContext);
  const [activeCountrySubTab, setActiveCountrySubTab] = useState('sellingCountry');
  const [sellingSkus, setSellingSkus] = useState([]);
  const [intermediarySkus, setIntermediarySkus] = useState([]);
  const [buyingSkus, setBuyingSkus] = useState([]);
  const [sellingCountry, setSellingCountry] = useState();
  const [pirData, setpirData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { producing_plant_data } = info;

  useEffect(() => {
    if (
      activeTab === 'tpSetup' &&
      activeSubTab === 'tpErpSetup' &&
      producing_plant_data.length > 0
    ) {
      if (
        ['sellingCountry', 'intermediaryCountry', 'buyingCountry'].includes(activeCountrySubTab)
      ) {
        fetchTPERPSetup();
      } else if (activeCountrySubTab === 'sellingCountry') {
        fetchSellingCountry();
      } else if (activeCountrySubTab === 'pirSetup') {
        fetchPIRSetup();
      }
    }
  }, [activeTab, activeSubTab, producing_plant_data, activeCountrySubTab]);

  const fetchSellingCountry = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/costing-request/get-selling-country/${crNumber}`);
      setSellingCountry(response.data.data);
    } catch (e) {
      console.error('Error fetching selling country:', e);
    }
  };

  const fetchTPERPSetup = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/costing-request/tp-erp-setup/${crNumber}`);
      const allData = [];
      const noData = [];
      response.data.data.forEach((item) => {
        if (item.IntCrTpErps.length > 0) {
          item.IntCrTpErps.forEach((erp) => {
            const status =
              String(erp?.status).toLowerCase() === 'pending' ? 'In Progress' : erp?.status;
            const isCompleted = status === 'Closed';
            const isEditable = !isCompleted;
            const commonData = {
              sku_number: item?.int_cr_sku_selector?.sku,
              sku_description: item?.int_cr_sku_selector?.sku_description,
              producing_plant: item?.int_cr_producing_plant?.producing_plant,
              status: status,
              logs: item.IntCrStepStatusLogs,
              assignedTo: crAssignedSpocs(producing_plant_data[0].producing_country),
              spoc: item.step_assigned?.name || 'N/A',
              step_id: item.step_id,
              id: erp?.id,
              sku_id: item?.sku_id,
              producing_plant_id: item.producing_plant_id,
              sales_org: erp.sales_org,
              distribution_channel: erp?.distribution_channel,
              condition_record_id: erp?.id,
              indicative_db: erp?.indicative_db,
              country: erp?.country,
              key_combination: erp?.key_combination || null,
              condition_type: erp?.condition_type || null,
              cost_per_hl: parseFloat(erp?.cost_per_hl || 0).toFixed(4) || 0,
              cost_per_100hl: parseFloat(erp?.cost_per_100hl || 0).toFixed(2) || 0,
              stock_price: parseFloat(erp?.stock_price || 0).toFixed(2) || 0,
              currency_key: erp?.currency_key,
              isEditable: isEditable,
              isSaved: isCompleted,
              valid_from: moment(erp.valid_from).isValid()
                ? moment(erp.valid_from).format('DD-MM-YYYY')
                : null,
              valid_to: moment(erp.valid_to).isValid()
                ? moment(erp.valid_to).format('DD-MM-YYYY')
                : null,
              completed_on: moment(erp?.completed_on).isValid()
                ? moment(erp?.completed_on).format('DD-MM-YYYY')
                : 'N/A',
              per: erp.per,
              unit_of_measure: erp.unit_of_measure,
              retryEnabled: false,
            };

            allData.push(commonData);
          });
        } else {
          const status = (item.status || '').toLowerCase();
          const data = {
            sku_number: item.int_cr_sku_selector.sku,
            sku_description: item.int_cr_sku_selector.sku_description,
            producing_plant: item.int_cr_producing_plant.producing_plant,
            status:
              status === 'not started'
                ? 'Not Started'
                : status === 'closed'
                ? 'Completed'
                : status === 'force closed'
                ? 'Force Closed'
                : status === 'force cancelled'
                ? 'Force Cancelled'
                : 'Pending',
            logs: item.IntCrStepStatusLogs,
            assignedTo: crAssignedSpocs(producing_plant_data[0].producing_country),
            spoc: item.step_assigned?.name || 'N/A',
            step_id: item.step_id,
          };
          noData.push(data);
        }
      });

      if (noData.length > 0) {
        setSellingSkus(noData);
        setIntermediarySkus(noData);
        setBuyingSkus(noData);
      } else {
        // Filter after collecting
        const selling = allData.filter((sku) => sku.indicative_db === 'in-zone');
        const intermediary = allData.filter((sku) => sku.indicative_db === 'elbe');
        const buying = allData.filter((sku) => sku.indicative_db === 'out-zone');
        setSellingSkus(selling);
        setIntermediarySkus(intermediary);
        setBuyingSkus(buying);
      }
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPIRSetup = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/costing-request/pir_data/${crNumber}`);
      if (response.data.success) {
        const allData = response.data.data || [];
        setpirData(allData); // store in state
      } else {
        console.warn('Failed to fetch PIR data:', response.data.message);
        setpirData([]);
      }
    } catch (error) {
      console.error('Error fetching PIR data:', error);
      setpirData([]); // fallback to empty array
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      name: 'Selling country',
      value: 'sellingCountry',
      component: (props) => (
        <SellingCountry
          {...props}
          skus={sellingSkus}
          refreshSkus={fetchTPERPSetup}
          sellingCountry={sellingCountry}
        />
      ),
    },
    {
      name: 'Intermediary country',
      value: 'intermediaryCountry',
      component: (props) => (
        <IntermediaryCountry {...props} skus={intermediarySkus} refreshSkus={fetchTPERPSetup} />
      ),
    },
    {
      name: 'Buying Country',
      value: 'buyingCountry',
      component: (props) => (
        <BuyingCountry {...props} skus={buyingSkus} refreshSkus={fetchTPERPSetup} />
      ),
    },
    {
      name: 'PIR Setup',
      value: 'pirSetup',
      component: (props) => (
        <PIRSetup {...props} parentLoading={loading} pirData={pirData} refreshPIR={fetchPIRSetup} />
      ),
    },
  ];

  const activeTabComponent = tabs.find((tab) => tab.value === activeCountrySubTab)?.component;

  return (
    <>
      <Tabs
        keepMounted={false}
        value={activeCountrySubTab}
        onTabChange={(value) => setActiveCountrySubTab(value)}
        variant='unstyled'
        styles={tabStyles}
        className='prod-dev-tabs'
        color='#e5b611'
      >
        <Tabs.List>
          {tabs.map((tab, index) => (
            <Tabs.Tab value={tab.value} key={index} className='menu-tabs'>
              {tab.name}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value={activeCountrySubTab}>
          {activeTabComponent &&
            activeTabComponent({
              activeCountrySubTab,
              activeSubTab,
              setLogsModal,
            })}
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

export default TpErpSetup;
